<template>
    <v-container>
        <back-button fixed/>
        <v-row align="center">
            <v-col cols="11" md="12" align="center">
                <v-btn
                    v-for="(button,i) in buttons"
                    :key="i"
                    class="kimsa-elevation normal-btn button-container"
                    text color="accent"
                    @click="onActionButton(button.action)"
                >
                    {{ button.name }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <select-option-students only-groups @change="currentGroupBy = $event"/>
                <v-data-table
                    :headers="headers"
                    :loading="loading"
                    :items="items"
                    :items-per-page="10"
                    @page-count="pageCount = $event"
                    :page.sync="page"
                    :mobile-breakpoint="0"
                    hide-default-footer
                    :search="search"
                    sort-by="name"
                    :loading-text="$t('common.loading')"
                    :no-data-text="$t('common.not_found')"
                >
                    <template v-slot:item="{ item }">
                        <tr class="kimsa-elevation">
                            <!-- NAME -->
                            <td style="border: none" class="py-2">
                                {{ item.name }}
                            </td>
                            <!-- SUBGROUPS -->
                            <td style="border: none" v-if="currentGroupBy === 'team'">
                                {{ item.quantitySubgroups }}
                            </td>
                            <!-- STUDENTS -->
                            <td style="border: none">
                                {{ item.quantityStudents }}
                            </td>
                            <!-- BUTTONS -->
                            <td style="border: none">
                                <v-btn class="mr-1 mt-1" fab small depressed @click="editItem(item)">
                                    <v-icon>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn class="mr-1 mt-1" fab small depressed @click="showDeleteConfirm(item)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </template>

                    <template v-slot:footer>
                        <v-row no-gutters>
                            <v-col cols="12" md="7" lg="8">
                                <kimsa-text-field
                                    class="mt-2 ml-3"
                                    @change="search = $event"
                                    :value="search"
                                    placeholder="Buscar..."
                                    color="grey" dense outlined
                                />
                            </v-col>
                            <v-col cols="12" md="5" lg="4">
                                <kimsa-pagination
                                    v-if="pageCount > 0"
                                    @page="page = $event"
                                    :pageCount="pageCount"
                                    :total-visible="isMobile ? 5 : 7"
                                />
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>

                <kimsa-confirm
                    ref="delete"
                    :title="$t('common.delete_group')"
                    :content="$t('common.delete_group_subtext')"
                    :loading="loadingDelete"
                    :persistent="false"
                    @confirm="onConfirmDelete"
                    @cancel="currentSelection = null"
                    :close-on-confirm="false"
                    :alert-mode="true"
                />

                <create-group-students-dialog ref="createGroup"/>
                <create-team-students-dialog ref="createTeam"/>

            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import {mapActions, mapState} from "vuex";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import {db, FieldValue} from "@/utils/Firebase";
import CreateGroupStudentsDialog from "@/components/coach/CreateGroupStudentsDialog";
import CreateTeamStudentsDialog from "@/components/coach/CreateTeamStudentsDialog";
import {deleteAssignedRoutinesGroupCollection, deleteGroupTeam, removeStudentsFromGroup} from "@/utils/Exercises";
import BackButton from "@/components/common/BackButton";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";

export default {
    name: "Groups",
    components: {
        KimsaPagination,
        KimsaTextField,
        SelectOptionStudents, BackButton, CreateTeamStudentsDialog, CreateGroupStudentsDialog, KimsaConfirm
    },
    data: (vm) => ({
        currentGroupBy: 'group',
        loading: false,
        loadingDelete: false,
        currentSelection: null,
        search: '',
        page: 1,
        pageCount: 0,
        buttons: [
            {name: vm.$t('common.add_group'), action: 'showCreateGroupDialog'},
            {name: vm.$t('common.add_team'), action: 'showCreateTeamDialog'},
        ]
    }),
    computed: {
        ...mapState(['groups', 'teams']),
        headers(vm = this) {
            let headers = [
                {text: vm.$t('common.name'), value: 'name'},
                {text: vm.$t('common.subgroups'), value: 'quantitySubgroups'},
                {text: vm.$t('common.students'), value: 'quantityStudents'},
                {text: '', value: 'actions', sortable: false},
            ]
            if (vm.currentGroupBy === 'group') headers = headers.filter(header => header.value !== 'quantitySubgroups')
            return headers
        },
        items(vm = this) {
            if (vm.currentGroupBy === 'group') {
                return vm.groups.map(group => {
                    return {
                        id: group.id,
                        name: group.name,
                        quantityStudents: group.students.length || 0,
                        students: group.students,
                    }
                })
            }
            return vm.teams.map(team => {
                let quantityStudents = 0
                team.groups.forEach(group => {
                    quantityStudents += group.students.length || 0
                })

                return {
                    id: team.id,
                    name: team.name,
                    groups: team.groups,
                    quantitySubgroups: team.groups.length || 0,
                    quantityStudents: quantityStudents,
                }
            })
        }
    },
    async mounted() {
        const vm = this
        if (vm.groups.length === 0) await vm.bindGroups(vm.currentUser.uid)
        if (vm.teams.length === 0) await vm.bindTeams(vm.currentUser.uid)
    },
    methods: {
        ...mapActions(['bindGroups', 'bindTeams']),
        async onConfirmDelete() {
            const vm = this
            vm.loadingDelete = true

            if (vm.currentGroupBy === 'group') {
                await deleteAssignedRoutinesGroupCollection(vm.currentSelection.id)
                await removeStudentsFromGroup(vm.currentSelection.students || [])
                await deleteGroupTeam(vm.currentSelection.id, 'groups')

                await db.collection('users').doc(vm.currentUser.uid).update({
                    groupsQuantity: FieldValue.increment(-1),
                })
            } else {
                for (let group of vm.currentSelection.groups) {
                    await deleteAssignedRoutinesGroupCollection(group.id, vm.currentSelection.id)
                    await removeStudentsFromGroup(group.students || [])
                }
                await deleteGroupTeam(vm.currentSelection.id, 'teams')

                await db.collection('users').doc(vm.currentUser.uid).update({
                    teamsQuantity: FieldValue.increment(-1),
                })
            }

            vm.loadingDelete = false
            vm.$refs.delete.hide()
        },
        showDeleteConfirm(currentSelection) {
            const vm = this
            vm.currentSelection = currentSelection
            vm.$refs.delete.show()
        },
        editItem(currentSelection) {
            const vm = this
            if (vm.currentGroupBy === 'group') return vm.$refs.createGroup.loadGroup(currentSelection)
            return vm.$refs.createTeam.loadTeam(currentSelection)
        },
        showCreateGroupDialog() {
            const vm = this
            vm.$refs.createGroup.show()
        },
        showCreateTeamDialog() {
            const vm = this
            vm.$refs.createTeam.show()
        },
        onActionButton(method) {
            this[method]()
        }
    },
}
</script>

<style scoped>

.button-container {
    border: 1px solid currentColor;
    border-radius: 0;
}

.button-container:hover {
    background: var(--v-accent-base);
    color: #FFFFFF !important;
}

>>>.v-data-table-header > tr {
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
}

>>>.v-data-table-header > tr > th {
    border: none !important;
}

>>>.v-data-table-header > tr > th > span {
    font-weight: bold !important;
}

</style>